import CancelRoundedIcon from '@mui/icons-material/CancelRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type {
  EmployerVerificationCheckpoint,
  PersistedGateCheckpointDto,
} from '../../api/employerVerificationDashboardApi';
import {
  formatGateStepLabel,
  resolvedCheckpointDisplayStatus,
  resolvedGateDisplayStatus,
} from '../../lib/employerVerificationGateAudit';
import {
  formatGateReasonReadableFlow,
  investigationCauseFromFailClause,
  parseFailReasonSignalLines,
} from '../../lib/employerVerificationGateReasonFormat';
import {
  gateInvestigationOutcomeLabel,
  gateMeta,
  GATE_ORDER_PIPE,
  gateStatusShort,
  type GateKey,
} from '../../lib/employerVerificationCopy';

export function parseGateCheckpointAudit(raw: unknown): Record<string, PersistedGateCheckpointDto[]> | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  return raw as Record<string, PersistedGateCheckpointDto[]>;
}

export function friendlyGateStatusLabel(statusLabel: string): string {
  const u = statusLabel.trim().toUpperCase();
  switch (u) {
    case 'PASS':
      return 'Pass';
    case 'FAIL':
      return 'Action needed';
    case 'REVIEW':
      return 'Review';
    case 'NOT_RUN':
      return 'Skipped';
    case 'NOT_APPLICABLE':
      return 'Not required';
    case 'UNKNOWN':
      return 'Unclear';
    default:
      return statusLabel || 'Unknown';
  }
}

export function gateStatusChip(raw: string | null | undefined): {
  label: string;
  color: 'success' | 'error' | 'warning' | 'default';
} {
  const u = (raw ?? '').trim().toUpperCase();
  if (u === 'PASS') return { label: 'PASS', color: 'success' };
  if (u === 'FAIL') return { label: 'FAIL', color: 'error' };
  if (u === 'REVIEW') return { label: 'REVIEW', color: 'warning' };
  return { label: u || 'NOT_RUN', color: 'default' };
}

export function firstAttentionGateKey(gates: Record<string, string | null>): GateKey | null {
  for (const gate of GATE_ORDER_PIPE) {
    const v = gates[gate];
    const statusRaw = typeof v === 'string' ? v : null;
    const chip = gateStatusChip(statusRaw);
    if (chip.color === 'error' || chip.color === 'warning') return gate;
  }
  return null;
}

export function normalizedConfidenceScore(score: number): number {
  if (score > 1 && score <= 100) return score / 100;
  return Math.min(1, Math.max(0, score));
}

export function confidenceBucketLabel(score01: number): string {
  if (score01 >= 0.75) return 'High';
  if (score01 >= 0.4) return 'Medium';
  return 'Low';
}

function confidenceMeterFilledSegments(score01: number): number {
  return Math.max(1, Math.min(5, Math.round(score01 * 5)));
}

export function DrawerConfidenceMeter({ score01, label }: { score01: number; label: string }) {
  const filled = confidenceMeterFilledSegments(score01);
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.04 }}>
        Confidence
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
        {label}
      </Typography>
      <Stack direction="row" spacing={0.35} alignItems="center" aria-hidden sx={{ pt: 0.25 }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: 10,
              height: 6,
              borderRadius: 0.5,
              bgcolor: (theme) => {
                if (i >= filled) return alpha(theme.palette.divider, 0.9);
                if (label === 'High') return alpha(theme.palette.success.main, 0.72);
                if (label === 'Low') return alpha(theme.palette.warning.main, 0.72);
                return alpha(theme.palette.primary.main, 0.55);
              },
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}

export function splitRecommendedActionsForDrawer(text: string, maxBullets = 4): string[] {
  const t = text.trim();
  if (!t) return [];
  const parts = t.split(/\.\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return [t.endsWith('.') ? t : `${t}.`];
  return parts.slice(0, maxBullets).map((p) => (p.endsWith('.') ? p : `${p}.`));
}

export function recommendedNextStepCopy(storedFinal: string | null): string | null {
  const u = (storedFinal ?? '').trim().toUpperCase();
  if (u === 'REJECTED') {
    return 'Verify employer legal name with the applicant. Gather employment proof if missing. Retry after registry data updates.';
  }
  if (u === 'MANUAL_REVIEW') {
    return 'Review employment documentation and employer contact details. Escalate if policy requires a second pair of eyes.';
  }
  return null;
}

export function auditForSingleGate(
  full: Record<string, PersistedGateCheckpointDto[]> | null,
  gate: GateKey,
): Record<string, PersistedGateCheckpointDto[]> | null {
  if (!full) return null;
  return { [gate]: full[gate] ?? [] };
}

export function formatPublishedStatusForDisplay(statusLabel: string): string {
  const u = statusLabel.trim().toUpperCase();
  if (!u || u === 'NOT_RUN') return 'Not run';
  if (u === 'NOT_APPLICABLE') return 'Not applicable';
  if (u === 'PASS') return 'Passed';
  if (u === 'FAIL') return 'Failed';
  if (u === 'REVIEW') return 'Needs review';
  return statusLabel.trim() || 'Unknown';
}

function publishedSummaryIsPipelineNoise(summary: string, statusLabel: string): boolean {
  const s = summary.trim();
  if (!s) return true;
  const lower = s.toLowerCase();
  if (/^stored summary:\s*$/i.test(s)) return true;
  if (/^no saved gate status/i.test(lower)) return true;
  const statusU = statusLabel.trim().toUpperCase();
  if (
    (statusU === 'NOT_RUN' || statusU === 'NOT_APPLICABLE') &&
    s.length < 80 &&
    !/[.!?]/.test(s)
  ) {
    return true;
  }
  return false;
}

export function filterMeaningfulPublishedCheckpoints(
  checkpoints: EmployerVerificationCheckpoint[],
): EmployerVerificationCheckpoint[] {
  return checkpoints.filter((cp) => {
    const summary = cp.adminSummary?.trim() ?? '';
    if (!summary) return false;
    return !publishedSummaryIsPipelineNoise(summary, cp.statusLabel);
  });
}

export function buildGateSkippedNotice(params: {
  gateTitle: string;
  gateStatusRaw: string | null | undefined;
  auditSteps: PersistedGateCheckpointDto[] | undefined;
  published: EmployerVerificationCheckpoint[];
}): string | null {
  const { gateTitle, gateStatusRaw, auditSteps, published } = params;
  const statusU = (gateStatusRaw ?? '').trim().toUpperCase();
  const skipped = !statusU || statusU === 'NOT_RUN' || statusU === 'NOT_APPLICABLE';
  if (!skipped) return null;
  if ((auditSteps?.length ?? 0) > 0) return null;
  if (filterMeaningfulPublishedCheckpoints(published).length > 0) return null;
  return `No ${gateTitle.toLowerCase()} evaluation output was generated for this application.`;
}

function normalizeForCompare(s: string): string {
  return s.replace(/\s+/g, ' ').trim().toLowerCase();
}

function oneSentence(text: string, maxLen = 220): string {
  const t = formatGateReasonReadableFlow(text).trim();
  if (!t) return '';
  const dot = t.indexOf('. ');
  if (dot > 0 && dot < maxLen) return t.slice(0, dot + 1).trim();
  if (t.length <= maxLen) return t.endsWith('.') ? t : `${t}.`;
  return `${t.slice(0, Math.max(0, maxLen - 1)).trim()}…`;
}

function firstAttentionClauseFromReason(full: string | null | undefined): string | null {
  const t = full?.trim();
  if (!t) return null;
  const lines = t.split(/\r?\n+/).map((l) => l.trim()).filter(Boolean);
  const hit = lines.find((l) => / — FAIL:/i.test(l) || / — REVIEW:/i.test(l));
  if (hit) return oneSentence(hit);
  return oneSentence(t);
}

function gateMessageLooksLikeCheckpointReplay(
  message: string,
  steps: PersistedGateCheckpointDto[] | undefined,
): boolean {
  if (!steps?.length) return false;
  const lines = message.split(/\r?\n+/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return false;
  const statusHits = lines.filter(
    (l) => /\b(PASS|FAIL|REVIEW)\b/i.test(l) || / — (PASS|FAIL|REVIEW):/i.test(l),
  );
  return statusHits.length >= Math.min(2, lines.length);
}

export function shouldShowGateNarrativeInRawEvidence(
  gateMessage: string | null | undefined,
  failReason: string | null | undefined,
  steps: PersistedGateCheckpointDto[] | undefined,
): boolean {
  const msg = (gateMessage ?? '').trim();
  if (!msg) return false;
  const fr = (failReason ?? '').trim();
  if (fr && normalizeForCompare(msg) === normalizeForCompare(fr)) return false;
  if (gateMessageLooksLikeCheckpointReplay(msg, steps)) return false;
  return true;
}

export type InvestigationSignal = {
  label: string;
  status: 'pass' | 'fail' | 'review' | 'skipped';
  hardStop?: boolean;
  failureDetail?: string;
};

export type GateInvestigationBrief = {
  title: string;
  cause: string;
  severityCaption?: string;
  failureReason?: string;
  supportingSignals: InvestigationSignal[];
  impact?: string;
};

function stepStatusToSignal(status: string): InvestigationSignal['status'] {
  const u = status.trim().toUpperCase();
  if (u === 'PASS') return 'pass';
  if (u === 'FAIL') return 'fail';
  if (u === 'REVIEW') return 'review';
  return 'skipped';
}

function extractFailureDetail(reason: string): string | undefined {
  const t = reason.trim();
  if (!t) return undefined;
  const colon = t.indexOf(':');
  if (colon >= 0) {
    const tail = t.slice(colon + 1).trim();
    if (tail && tail.length <= 64) return tail.split(/\s+/)[0] ?? tail;
  }
  const token = t.match(/\b(NOT_FOUND|NOT FOUND|DISSOLVED|STRUCK OFF)\b/i);
  return token ? token[0].replace(/\s+/g, '_').toUpperCase() : undefined;
}

function signalsFromAuditSteps(steps: PersistedGateCheckpointDto[]): InvestigationSignal[] {
  return steps.map((step) => {
    const status = stepStatusToSignal(resolvedCheckpointDisplayStatus(step));
    return {
      label: formatGateStepLabel(step.step),
      status,
      hardStop: status === 'fail' && step.isHardStop,
      failureDetail: status === 'fail' || status === 'review' ? extractFailureDetail(step.reason) : undefined,
    };
  });
}

function signalsFromFailReason(fr: string): InvestigationSignal[] {
  return parseFailReasonSignalLines(fr).map((row) => ({
    label: row.label,
    status: row.status,
    failureDetail: row.detail ? extractFailureDetail(row.detail) ?? row.detail.slice(0, 64) : undefined,
  }));
}

function failReasonMatchesStepReasons(fr: string, steps: PersistedGateCheckpointDto[]): boolean {
  if (!steps.length) return false;
  const frN = normalizeForCompare(fr);
  const stepsN = normalizeForCompare(
    steps.map((s) => formatGateReasonReadableFlow(s.reason)).join(' '),
  );
  return frN === stepsN || (frN.length > 20 && stepsN.includes(frN));
}

export function filterPublishedForTechnicalMetadata(
  published: EmployerVerificationCheckpoint[],
  steps: PersistedGateCheckpointDto[],
): EmployerVerificationCheckpoint[] {
  if (!steps.length) return published;
  return published.filter((cp) => {
    const n = normalizeForCompare(cp.adminSummary ?? '');
    if (!n) return false;
    return !steps.some((s) => {
      const r = normalizeForCompare(formatGateReasonReadableFlow(s.reason));
      return r && (n === r || n.includes(r) || r.includes(n));
    });
  });
}

export function buildGateInvestigationBrief(params: {
  gateStatusRaw: string | null | undefined;
  steps: PersistedGateCheckpointDto[] | undefined;
  failReason: string | null | undefined;
  parseFailReason?: boolean;
}): GateInvestigationBrief | null {
  const { gateStatusRaw, steps, failReason, parseFailReason = false } = params;
  const auditSteps = steps ?? [];
  const statusU = resolvedGateDisplayStatus(gateStatusRaw, auditSteps);
  const failStep = auditSteps.find(
    (s) => resolvedCheckpointDisplayStatus(s) === 'FAIL',
  );
  const reviewStep = auditSteps.find(
    (s) => resolvedCheckpointDisplayStatus(s) === 'REVIEW',
  );
  const passSteps = auditSteps.filter(
    (s) => resolvedCheckpointDisplayStatus(s) === 'PASS',
  );
  const fr = (failReason ?? '').trim();
  const useFailReason = parseFailReason && fr && !failReasonMatchesStepReasons(fr, auditSteps);

  const hasAttention =
    statusU === 'FAIL' || statusU === 'REVIEW' || !!failStep || !!reviewStep || (useFailReason && !!fr);
  if (!hasAttention) return null;
  if (statusU === 'PASS' && !failStep && !reviewStep && !(useFailReason && fr)) return null;

  let supportingSignals: InvestigationSignal[] =
    auditSteps.length > 0 ? signalsFromAuditSteps(auditSteps) : useFailReason ? signalsFromFailReason(fr) : [];

  if (supportingSignals.length === 0 && useFailReason) {
    const parsed = parseFailReasonSignalLines(fr);
    if (parsed.length === 0) {
      const clause = firstAttentionClauseFromReason(fr);
      if (clause) {
        supportingSignals = [
          {
            label: 'Evaluation',
            status: /REVIEW/i.test(clause) ? 'review' : 'fail',
            failureDetail: extractFailureDetail(clause),
          },
        ];
      }
    }
  }

  let cause = '';
  if (failStep) {
    const readable = formatGateReasonReadableFlow(failStep.reason);
    cause = readable
      ? oneSentence(readable)
      : `${formatGateStepLabel(failStep.step)} did not pass.`;
  } else if (reviewStep) {
    const readable = formatGateReasonReadableFlow(reviewStep.reason);
    cause = readable
      ? oneSentence(readable)
      : `${formatGateStepLabel(reviewStep.step)} needs review.`;
  } else if (useFailReason) {
    const clause = firstAttentionClauseFromReason(fr);
    cause = clause ? investigationCauseFromFailClause(clause) : oneSentence(fr);
  }

  if (!cause.trim()) return null;

  const failingSignal = supportingSignals.find((s) => s.status === 'fail') ?? supportingSignals.find((s) => s.status === 'review');
  const failureReason =
    failingSignal?.failureDetail ??
    (failStep ? extractFailureDetail(failStep.reason) : undefined) ??
    (useFailReason
      ? parseFailReasonSignalLines(fr).find((r) => r.status === 'fail')?.detail
      : undefined);

  let severityCaption: string | undefined;
  if (failingSignal?.hardStop) severityCaption = 'Hard stop';
  else if (failingSignal?.status === 'fail') severityCaption = 'Failure reason';

  let impact: string | undefined;
  if (passSteps.length > 0 && (failStep || reviewStep || statusU === 'FAIL' || statusU === 'REVIEW')) {
    impact = 'Supporting signals matched, but the failed result prevented automatic approval.';
  }

  let title = 'Review rationale';
  if (statusU === 'FAIL' || failStep) title = 'Why this failed';
  else if (statusU === 'REVIEW' || reviewStep) title = 'Why this requires review';

  return {
    title,
    cause,
    severityCaption,
    failureReason: failureReason?.trim() || undefined,
    supportingSignals,
    impact,
  };
}

const TERMINAL_FAILURE_RE = /NOT_FOUND|NOT FOUND|DISSOLVED|STRUCK\s+OFF/i;
const RECOVERABLE_FAILURE_RE =
  /UNAVAILABLE|TIMEOUT|TEMPORARY|TRANSIENT|503|502|504|RATE\s*LIMIT|SERVICE\s*UNAVAILABLE/i;

export type InvestigationSidebarPanel = {
  statusLabel: string;
  recommendedActions: string[];
  decisionLine?: string;
  failureReason?: string;
  hardStopApplied?: boolean;
  closedMessage?: string;
  skippedMessage?: string;
};

function failureReasonBlob(
  steps: PersistedGateCheckpointDto[],
  brief: GateInvestigationBrief | null,
  failReason?: string | null,
): string {
  const failStep = steps.find((s) => s.status.trim().toUpperCase() === 'FAIL');
  return [
    failStep?.reason,
    failReason,
    brief?.failureReason,
    brief?.cause,
    ...(brief?.supportingSignals.map((s) => s.failureDetail) ?? []),
  ]
    .filter(Boolean)
    .join(' ');
}

export function isTerminalGateFailure(params: {
  steps: PersistedGateCheckpointDto[];
  brief: GateInvestigationBrief | null;
  failReason?: string | null;
}): boolean {
  const { steps, brief, failReason } = params;
  const failStep = steps.find((s) => s.status.trim().toUpperCase() === 'FAIL');
  if (failStep?.isHardStop) return true;
  if (brief?.supportingSignals.some((s) => s.hardStop)) return true;
  return TERMINAL_FAILURE_RE.test(failureReasonBlob(steps, brief, failReason));
}

export function isRecoverableGateFailure(params: {
  steps: PersistedGateCheckpointDto[];
  brief: GateInvestigationBrief | null;
  failReason?: string | null;
}): boolean {
  if (isTerminalGateFailure(params)) return false;
  const { steps, brief, failReason } = params;
  if (!steps.some((s) => s.status.trim().toUpperCase() === 'FAIL')) return false;
  return RECOVERABLE_FAILURE_RE.test(failureReasonBlob(steps, brief, failReason));
}

function formatSidebarFailureReason(
  steps: PersistedGateCheckpointDto[],
  brief: GateInvestigationBrief | null,
): string | undefined {
  const failStep = steps.find((s) => s.status.trim().toUpperCase() === 'FAIL');
  const detail =
    (failStep ? extractFailureDetail(failStep.reason) : undefined) ?? brief?.failureReason;
  if (!detail) return undefined;
  if (failStep) {
    return `${formatGateStepLabel(failStep.step)} returned ${detail}`;
  }
  return detail;
}

export function buildInvestigationSidebarPanel(params: {
  gateStatusRaw: string | null | undefined;
  steps: PersistedGateCheckpointDto[] | undefined;
  investigationBrief: GateInvestigationBrief | null;
  gateSkippedNotice: string | null;
  suggestedActionBullets: string[];
  storedFinal: string | null;
  failReason?: string | null;
}): InvestigationSidebarPanel {
  const {
    gateStatusRaw,
    steps,
    investigationBrief,
    gateSkippedNotice,
    suggestedActionBullets,
    storedFinal,
    failReason,
  } = params;
  const auditSteps = steps ?? [];
  const statusU = (gateStatusRaw ?? '').trim().toUpperCase();
  const statusLabel = gateInvestigationOutcomeLabel(gateStatusRaw);
  const failStep = auditSteps.find((s) => s.status.trim().toUpperCase() === 'FAIL');
  const reviewStep = auditSteps.find((s) => s.status.trim().toUpperCase() === 'REVIEW');
  const failureReason = formatSidebarFailureReason(auditSteps, investigationBrief);
  const hardStopApplied =
    !!failStep?.isHardStop ||
    !!investigationBrief?.supportingSignals.some((s) => s.hardStop);

  if (!statusU || statusU === 'NOT_RUN' || statusU === 'NOT_APPLICABLE') {
    return {
      statusLabel,
      recommendedActions: [],
      skippedMessage: gateSkippedNotice ?? 'No evidence generated for this gate.',
    };
  }

  if (statusU === 'PASS' && !failStep && !reviewStep) {
    return {
      statusLabel,
      recommendedActions: [],
      closedMessage: 'Verification completed successfully.',
    };
  }

  if (statusU === 'REVIEW' || reviewStep) {
    let recommendedActions =
      suggestedActionBullets.length > 0 ? [...suggestedActionBullets] : [];
    if (recommendedActions.length === 0) {
      const finalU = (storedFinal ?? '').trim().toUpperCase();
      if (finalU === 'MANUAL_REVIEW') {
        const copy = recommendedNextStepCopy(storedFinal);
        recommendedActions = copy ? splitRecommendedActionsForDrawer(copy) : [];
      }
    }
    return {
      statusLabel,
      recommendedActions,
      decisionLine: investigationBrief?.cause,
    };
  }

  if (statusU === 'FAIL' || failStep) {
    const terminal = isTerminalGateFailure({
      steps: auditSteps,
      brief: investigationBrief,
      failReason,
    });
    const decisionLine =
      investigationBrief?.cause ??
      (failStep
        ? oneSentence(formatGateReasonReadableFlow(failStep.reason)) ||
          `${formatGateStepLabel(failStep.step)} did not pass.`
        : undefined);

    if (terminal) {
      return {
        statusLabel,
        recommendedActions: [],
        decisionLine,
        failureReason,
        hardStopApplied,
        closedMessage: 'No additional action required.',
      };
    }

    if (
      isRecoverableGateFailure({
        steps: auditSteps,
        brief: investigationBrief,
        failReason,
      })
    ) {
      return {
        statusLabel,
        recommendedActions: ['Retry verification'],
        failureReason,
      };
    }

    return {
      statusLabel,
      recommendedActions: [],
      decisionLine,
      failureReason,
      hardStopApplied,
      closedMessage: 'No additional action required.',
    };
  }

  return { statusLabel, recommendedActions: [] };
}

export function GateInvestigationSignals({ signals }: { signals: InvestigationSignal[] }) {
  if (!signals.length) return null;
  return (
    <Stack spacing={0.75}>
      {signals.map((sig) => {
        const icon =
          sig.status === 'pass' ? (
            <CheckCircleRoundedIcon sx={{ fontSize: 18, color: 'success.main', flexShrink: 0 }} />
          ) : sig.status === 'fail' ? (
            <CancelRoundedIcon sx={{ fontSize: 18, color: 'error.main', flexShrink: 0 }} />
          ) : sig.status === 'review' ? (
            <WarningAmberRoundedIcon sx={{ fontSize: 18, color: 'warning.main', flexShrink: 0 }} />
          ) : (
            <WarningAmberRoundedIcon sx={{ fontSize: 18, color: 'text.disabled', flexShrink: 0 }} />
          );
        return (
          <Stack key={`${sig.label}-${sig.status}`} direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ pt: 0.1 }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.45 }}>
                {sig.label}
              </Typography>
              {sig.failureDetail ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.4 }}>
                  {sig.failureDetail}
                </Typography>
              ) : null}
              {sig.hardStop ? (
                <Typography variant="caption" color="error.main" sx={{ fontWeight: 700, display: 'block' }}>
                  Hard stop
                </Typography>
              ) : null}
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
}

export function GateOutcomePickerChips({
  gates,
  onSelect,
}: {
  gates: Record<string, string | null>;
  onSelect: (gate: GateKey) => void;
}) {
  const rows = GATE_ORDER_PIPE.map((gate) => {
    const v = gates[gate];
    const statusRaw = typeof v === 'string' ? v : null;
    const chip = gateStatusChip(statusRaw);
    const friendly = friendlyGateStatusLabel(chip.label);
    const attention = chip.color === 'error' || chip.color === 'warning';
    const label = `${gateMeta[gate].title}: ${gateStatusShort(statusRaw)}`;
    return { gate, chip, friendly, attention, label };
  });

  const attentionRows = rows.filter((r) => r.attention);
  const quietRows = rows.filter((r) => !r.attention);
  const quietPasses = quietRows.filter((r) => r.chip.color === 'success');
  const quietSkips = quietRows.filter((r) => r.chip.color !== 'success');
  const skipSummary =
    quietSkips.length > 1
      ? {
          count: quietSkips.length,
          titles: quietSkips.map((r) => gateMeta[r.gate].title).join(', '),
        }
      : null;

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} alignItems="center">
        {attentionRows.map((r) => (
          <Chip
            key={r.gate}
            size="small"
            label={r.label}
            color={r.chip.color}
            variant="filled"
            onClick={() => onSelect(r.gate)}
            sx={{
              fontWeight: 700,
              cursor: 'pointer',
              transition: 'box-shadow 0.18s ease, filter 0.18s ease, transform 0.18s ease',
              '&:hover': {
                boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.common.black, 0.12)}`,
                filter: 'brightness(0.97)',
              },
            }}
          />
        ))}
        {attentionRows.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No failing gates
          </Typography>
        ) : null}
      </Stack>
      {quietPasses.length > 0 || quietSkips.length > 0 ? (
        <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0.5} alignItems="center">
          {quietPasses.map((r) => (
            <Chip
              key={r.gate}
              size="small"
              variant="filled"
              color="success"
              label={r.label}
              onClick={() => onSelect(r.gate)}
              sx={{
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'box-shadow 0.18s ease, filter 0.18s ease, transform 0.18s ease',
                '&:hover': {
                  boxShadow: (theme) => `0 2px 6px ${alpha(theme.palette.common.black, 0.12)}`,
                  filter: 'brightness(0.97)',
                },
              }}
            />
          ))}
          {skipSummary ? (
            <Tooltip title={skipSummary.titles} placement="top" arrow>
              <Chip
                size="small"
                variant="outlined"
                label={`+${skipSummary.count} skipped checks`}
                sx={{
                  fontWeight: 600,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  bgcolor: 'action.hover',
                  transition: 'background-color 0.18s ease',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              />
            </Tooltip>
          ) : (
            quietSkips.map((r) => (
              <Chip
                key={r.gate}
                size="small"
                variant="outlined"
                color="default"
                label={`${gateMeta[r.gate].title}: ${r.friendly}`}
                onClick={() => onSelect(r.gate)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 500,
                  borderColor: 'divider',
                  color: 'text.secondary',
                  bgcolor: 'action.hover',
                  transition: 'background-color 0.18s ease',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              />
            ))
          )}
        </Stack>
      ) : null}
    </Stack>
  );
}
